import React, { useState, useEffect, useMemo, useRef } from 'react';
import { collection, query, getDocs, orderBy, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import styles from '../styles/TeamPerformanceReportPage.module.css'; // Reusing styles is efficient

const getDateString = (date) => {
    return new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
};

const LoginHistoryPage = () => {
    const { userRole } = useAuth();
    const [logs, setLogs] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [selectedDate, setSelectedDate] = useState(getDateString(new Date()));
    const [selectedUser, setSelectedUser] = useState('all');

    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            if (!isMounted.current) return;
            setLoading(true);
            try {
                // Fetch users for the filter dropdown
                const usersCol = collection(db, 'users');
                const usersSnapshot = await getDocs(query(usersCol, orderBy('name')));
                if (!isMounted.current) return;
                const usersList = usersSnapshot.docs.map(doc => ({ id: doc.id, name: doc.data().name || doc.data().email }));
                setAllUsers(usersList);

                // Fetch login logs
                const logsCol = collection(db, 'loginHistory');
                const logsQuery = query(logsCol, orderBy('timestamp', 'desc'));
                const logsSnapshot = await getDocs(logsQuery);
                if (!isMounted.current) return;
                const logsData = logsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    timestamp: doc.data().timestamp.toDate(),
                }));
                setLogs(logsData);

            } catch (err) {
                console.error("Error fetching login history data:", err);
                if (isMounted.current) setError("Failed to load data. Please try again.");
            } finally {
                if (isMounted.current) setLoading(false);
            }
        };
        fetchData();
    }, []);

    const filteredLogs = useMemo(() => {
        return logs.filter(log => {
            const logDate = getDateString(log.timestamp);
            const dateMatch = selectedDate ? logDate === selectedDate : true;
            const userMatch = selectedUser === 'all' ? true : log.userId === selectedUser;
            return dateMatch && userMatch;
        });
    }, [logs, selectedDate, selectedUser]);
    
    const todayForDateInput = useMemo(() => getDateString(new Date()), []);

    if (loading) return <div className={styles.loadingIndicator}>Loading Login History...</div>;
    if (error) return <div className={styles.pageContainer + ' ' + styles.errorMessage}>{error}</div>;

    return (
        <div className={styles.pageContainer}>
            <h2 className={styles.pageTitle}>Staff Login History</h2>
            <div className={styles.filtersContainer}>
                <div className={styles.filterGroup}>
                    <label htmlFor="log-date-filter" className={styles.dateFilterLabel}>Date:</label>
                    <input
                        type="date"
                        id="log-date-filter"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className={styles.dateInput}
                        max={todayForDateInput}
                    />
                </div>
                <div className={styles.filterGroup}>
                    <label htmlFor="user-filter" className={styles.dateFilterLabel}>Staff Member:</label>
                    <select
                        id="user-filter"
                        value={selectedUser}
                        onChange={(e) => setSelectedUser(e.target.value)}
                        className={styles.filterSelect}
                    >
                        <option value="all">All Staff</option>
                        {allUsers.map(user => (
                            <option key={user.id} value={user.id}>{user.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className={styles.reportTableContainer}>
                <table className={styles.reportTable}>
                    <thead>
                        <tr>
                            <th>Staff Member</th>
                            <th>Role</th>
                            <th>Login Time</th>
                            <th>Approx. Location</th>
                            <th>IP Address</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredLogs.length === 0 ? (
                            <tr>
                                <td colSpan="5" className={styles.noDataMessage}>No login records found for the selected filters.</td>
                            </tr>
                        ) : (
                            filteredLogs.map(log => (
                                <tr key={log.id}>
                                    <td>{log.userName}</td>
                                    <td>{log.userRole}</td>
                                    <td>{log.timestamp.toLocaleString()}</td>
                                    <td>{log.location?.city}, {log.location?.country}</td>
                                    <td>{log.ipAddress}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default LoginHistoryPage;